import { inject, Injectable, Injector, signal } from "@angular/core";
import { MaintenanceService } from "./maintenance.service";

const BULK_IMPORT_KEY = "mitp-bulk-import";

/** Console commands, available as `mitp.<command>()` once the app has started. */
export interface MitpConsoleCommands {
	enableBulkImport(): string;
	disableBulkImport(): string;
	adminAccess(): string;
}

declare global {
	interface Window {
		mitp?: MitpConsoleCommands;
	}
}

/**
 * Features hidden until someone turns them on from the browser console. The choice is kept in this
 * browser only (localStorage).
 *
 * - `mitp.enableBulkImport()` shows the "Import protocols" button in My protocols
 * - `mitp.disableBulkImport()` hides it again
 * - `mitp.adminAccess()` sets the maintenance screen aside, for a supreme admin to log in
 */
@Injectable({
	providedIn: "root",
})
export class HiddenFeaturesService {
	private injector = inject(Injector);
	readonly bulkImport = signal(this.read(BULK_IMPORT_KEY));

	constructor() {
		window.mitp = {
			enableBulkImport: () => this.setBulkImport(true),
			disableBulkImport: () => this.setBulkImport(false),
			// Looked up when called: this service starts with the app, before anything else is needed.
			adminAccess: () => this.injector.get(MaintenanceService).dismiss(),
		};
	}

	private setBulkImport(enabled: boolean): string {
		this.write(BULK_IMPORT_KEY, enabled);
		this.bulkImport.set(enabled);
		return enabled ? "Bulk import enabled" : "Bulk import disabled";
	}

	/** Storage can be blocked (private browsing, site data cleared): the feature then stays hidden. */
	private read(key: string): boolean {
		try {
			return localStorage.getItem(key) === "true";
		} catch {
			return false;
		}
	}

	private write(key: string, enabled: boolean): void {
		try {
			if (enabled) {
				localStorage.setItem(key, "true");
			} else {
				localStorage.removeItem(key);
			}
		} catch {
			// Not kept across reloads, but still applied to this page
		}
	}
}
